import React, { createContext, useContext, useState, useEffect } from 'react';

interface MobileAppContextType {
  isMobile: boolean;
  highlightedItems: Record<string, string[]>;
  setHighlight: (pagePath: string, itemId: string) => void;
  isHighlighted: (pagePath: string, itemId: string) => boolean;
}

const MobileAppContext = createContext<MobileAppContextType | undefined>(undefined);

export const MobileAppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [highlightedItems, setHighlightedItems] = useState<Record<string, string[]>>(() => {
    const saved = localStorage.getItem('devnectar_highlights');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    localStorage.setItem('devnectar_highlights', JSON.stringify(highlightedItems));
  }, [highlightedItems]);

  const setHighlight = (pagePath: string, itemId: string) => {
    if (!isMobile) return;
    setHighlightedItems(prev => ({
      ...prev,
      [pagePath]: [itemId] // Resetting for simplicity, keeping only one highlight per page as a common pattern, or use toggle
    }));
  };

  const isHighlighted = (pagePath: string, itemId: string) => {
    if (!isMobile) return false;
    return highlightedItems[pagePath]?.includes(itemId) || false;
  };

  return (
    <MobileAppContext.Provider value={{ isMobile, highlightedItems, setHighlight, isHighlighted }}>
      {children}
    </MobileAppContext.Provider>
  );
};

export const useMobileApp = () => {
  const context = useContext(MobileAppContext);
  if (context === undefined) {
    throw new Error('useMobileApp must be used within a MobileAppProvider');
  }
  return context;
};
